import NaicsSelector from "../../shared/components/NaicsSelector";
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, TextField, Button, Stack, Chip,
  Paper, Divider, MenuItem, FormControl, InputLabel, Select,
  Checkbox, ListItemText, LinearProgress,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import SaveIcon from "@mui/icons-material/Save";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BusinessIcon from "@mui/icons-material/Business";

import BackButton from "../../shared/components/BackButton";
import CompanyActivityTracker from "../../shared/components/CompanyActivityTracker";
import CompanyAutocomplete from "../../shared/components/CompanyAutoComplete";
import CountryDropdown from "../../shared/forms/CountriesDropDown";
import StateDropdown from "../../shared/forms/StateProvinceDropDown";
import SectorDropdown from "../../shared/forms/SectorDropDown";
import ProjectMultiSelectField from "./ProjectMultiSelectField";
import { PRIMARY_BUTTON_SX } from "../../theme/buttonstyles";
import { useAuth } from "../../providers/AuthProvider";
import api, { isCancel } from "../../services/api/axios";
import { addRecentItem } from "../home-dashboard/RecentItemsHelper";
import { PROJECT_TYPES } from "./ProjectTypes";
import {
  formatBroadSectorLabel,
} from "../bre/companies/BRECompanyHelpers";
import {
  normalizePhase,
  mapApiTypeToUi,
  buildProjectPayload,
  PHASES,
} from "./projectDetailsFormUtils";
import {
  SKILL_SETS,
  SITE_READINESS_OPTIONS,
  EXPANSION_TYPES,
  REASONS_FOR_ENGAGEMENT,
  RETENTION_RISKS,
  SUPPORT_NEEDED,
} from "./ProjectOptions";
import { BRAND_BLUE } from "../../theme/colors";

const BRAND_GREEN = "#648315";

const SECTION_HEADER_SX = {
  display: "flex", alignItems: "center", gap: 1.5, mb: 2.5,
};

const DATE_FIELD_SX = {
  "& .MuiPickersInputBase-root, & .MuiPickersOutlinedInput-root": { color: BRAND_BLUE },
  "& .MuiPickersSectionList-root": { padding: "9px 14px", color: BRAND_BLUE },
  "& .MuiInputBase-input": { color: `${BRAND_BLUE} !important`, WebkitTextFillColor: BRAND_BLUE, fontWeight: 500 },
  "& .MuiPickersSectionList-sectionContent, & .MuiPickersInputBase-sectionContent": {
    color: `${BRAND_BLUE} !important`, fontFamily: "inherit !important",
    fontSize: "1rem !important", fontWeight: "500 !important",
  },
  "& .MuiInputLabel-root": { color: BRAND_BLUE },
  "& .MuiIconButton-root": { color: BRAND_BLUE },
};

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  return String(value ?? "").trim().length > 0;
};

const getSectionCompletion = (record, fields) => {
  const completed = fields.filter((field) =>
    hasValue(field.getValue ? field.getValue(record) : record[field.key]),
  ).length;
  return { completed, total: fields.length };
};

function SectionHeader({ children }) {
  return (
    <Box sx={SECTION_HEADER_SX}>
      <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {children}
      </Typography>
    </Box>
  );
}

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const { token, user } = useAuth();

  const [project, setProject] = useState({
    project_name: "", company: "", company_id: null, company_source: "bre",
    city: "", country: "", state: "", sector: "", industry: "", naics_code: "", naics_text: "", industry_custom: "",
    investment_amount: "", jobs_created: "", start_date: "", end_date: "",
    project_manager: "", project_phase: isNew ? "Project Created" : "",
    project_type: "", target_site: "", site_readiness: "", property_owner: "",
    skill_sets: [], transportation_requirements: "", utility_requirements: "",
    retention_risks: [], support_needed: [], infrastructure_requirements: "",
    current_employees: "", expected_new_hires: "", expansion_type: "",
    expansion_square_footage: "", reason_for_engagement: "",
    square_footage: "", acreage: "", wage_rate: "",
    startup_stage: "", accelerator_program: "", funding_source: "", runway_months: "",
  });

  const [loading, setLoading] = useState(!isNew);

  useEffect(() => {
    if (isNew) { setLoading(false); return; }

    const ctrl = new AbortController();
    (async () => {
      try {
        const res = await api.get(`/projects/${id}`, { signal: ctrl.signal });
        const data = res.data || {};
        data.project_name = data.project_name ?? "";
        data.company_source = data.company_source || "bre";
        data.start_date = data.start_date?.split?.("T")?.[0] || "";
        data.end_date = data.end_date?.split?.("T")?.[0] || "";

        const toArray = (v) =>
          typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean)
          : Array.isArray(v) ? v : [];

        data.skill_sets = toArray(data.skill_sets);
        data.retention_risks = toArray(data.retention_risks);
        data.support_needed = toArray(data.support_needed);
        data.infrastructure_requirements = toArray(data.infrastructure_requirements).join(", ");
        data.transportation_requirements = data.transportation_requirements ?? "";
        data.utility_requirements = data.utility_requirements ?? "";
        data.project_type = mapApiTypeToUi(data.project_type);
        data.wage_rate = data.wage_rate == null ? "" : String(data.wage_rate);
        data.project_phase = normalizePhase(data.project_phase);
        data.company = data.company ?? "";
        data.naics_code = data.naics_code ?? "";
        data.naics_text = data.naics_text ?? "";
        data.industry_custom = data.industry_custom ?? (data.naics_text ? "" : data.industry ?? "");
        data.company_id = data.company_id ?? null;
        delete data.projected_jobs;
        setProject(data);
      } catch (err) {
        if (!isCancel(err)) console.error("Failed to fetch project", err);
      } finally {
        setLoading(false);
      }
    })();

    return () => ctrl.abort();
  }, [id, isNew]);

  useEffect(() => {
    if (!loading && !isNew && user?.id && id) {
      addRecentItem(user.id, "project", { id, name: project.company || "Project", company_name: project.company || null });
    }
  }, [loading, isNew, id, project?.company, user?.id]);

  const handleChange = useCallback(
    (field) => (e) => setProject((p) => ({ ...p, [field]: e.target.value })),
    [],
  );

  const handleMulti = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setProject((p) => ({
        ...p,
        [field]: typeof value === "string"
          ? value.split(",").map((s) => s.trim()).filter(Boolean)
          : value,
      }));
    },
    [],
  );

  const handleDateChange = (field) => (value) => {
    setProject((prev) => ({ ...prev, [field]: value?.isValid?.() ? value.format("YYYY-MM-DD") : "" }));
  };

  const getDateValue = (value) => {
    const parsed = value ? dayjs(value) : null;
    return parsed?.isValid?.() ? parsed : null;
  };

  const handleSubmit = useCallback(async () => {
    if (!token) { alert("You must be logged in to submit a project."); return; }
    if (isNew && !project.company_id) { alert("Please select a company before saving this project."); return; }

    const payload = buildProjectPayload(project);
    try {
      if (isNew) { await api.post("/projects/", payload); }
      else { await api.patch(`/projects/${id}`, payload); }
      navigate("/projects");
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data || err?.message;
      alert(`Failed to save project.\n\n${JSON.stringify(detail, null, 2)}`);
    }
  }, [id, isNew, navigate, project, token]);

  const uiType = String(project.project_type || "").toLowerCase();
  const isAttraction = uiType === "business_attraction";
  const isRetentionExpansion = ["business_retention_expansion", "business_expansion", "business_retention"].includes(uiType);
  const projectCompletion = React.useMemo(() => {
    const sections = {
      summary: getSectionCompletion(project, [
        { key: "project_name" },
        { key: "company" },
        { key: "project_type" },
        { key: "project_phase" },
        { key: "project_manager" },
        { key: "start_date" },
        { key: "end_date" },
      ]),
      location: getSectionCompletion(project, [
        { key: "country" },
        { key: "state" },
        { key: "city" },
      ]),
      details: getSectionCompletion(project, [
        { key: "sector" },
        { key: "industry" },
        { key: "investment_amount" },
        { key: "jobs_created" },
        { key: "wage_rate" },
        { key: "skill_sets" },
      ]),
    };

    if (isAttraction) {
      sections.site = getSectionCompletion(project, [
        { key: "target_site" },
        { key: "square_footage" },
        { key: "acreage" },
        { key: "site_readiness" },
        { key: "transportation_requirements" },
        { key: "utility_requirements" },
        { key: "infrastructure_requirements" },
        { key: "property_owner" },
      ]);
    }

    if (isRetentionExpansion) {
      sections.expansion = getSectionCompletion(project, [
        { key: "current_employees" },
        { key: "expected_new_hires" },
        { key: "expansion_type" },
        { key: "expansion_square_footage" },
        { key: "infrastructure_requirements" },
        { key: "transportation_requirements" },
        { key: "utility_requirements" },
      ]);
      sections.retention = getSectionCompletion(project, [
        { key: "retention_risks" },
        { key: "support_needed" },
        { key: "reason_for_engagement" },
      ]);
    }

    const completed = Object.values(sections).reduce(
      (sum, section) => sum + section.completed,
      0,
    );
    const total = Object.values(sections).reduce(
      (sum, section) => sum + section.total,
      0,
    );

    return {
      ...sections,
      completed,
      total,
      overall: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [isAttraction, isRetentionExpansion, project]);

  if (loading) return (
    <Box sx={{ p: 4, display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <Typography sx={{ color: BRAND_BLUE, fontWeight: 600 }}>Loading...</Typography>
    </Box>
  );

  return (
    <Box sx={{ p: 2, minHeight: "100%", boxSizing: "border-box", overflowX: "hidden", bgcolor: "#f0f4f8" }}>
      <Box sx={{ maxWidth: 1850, mx: "auto", width: "100%" }}>

        {/* HEADER CARD */}
        <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: "hidden", border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN})` }} />

          <Box sx={{ px: 3, pt: 2.5, pb: 0 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
              <BackButton to="/projects" />
              {user?.organization?.name && (
                <Chip label={user.organization.name} size="small"
                  sx={{ bgcolor: "rgba(0,51,108,0.06)", color: BRAND_BLUE, fontWeight: 600, fontSize: 12, border: "1px solid rgba(0,51,108,0.12)" }}
                />
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <Box sx={{ width: 46, height: 46, borderRadius: 2, bgcolor: "rgba(0,51,108,0.07)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <AssignmentIcon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 700, lineHeight: 1.2 }}>
                  {isNew ? "New Project" : project.project_name || project.company || "Project"}
                </Typography>
                {!isNew && project.company && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                    <BusinessIcon sx={{ fontSize: 14, color: BRAND_GREEN }} />
                    <Typography variant="body2" sx={{ color: BRAND_GREEN }}>{project.company}</Typography>
                  </Box>
                )}
                {!isNew && project.project_phase && (
                  <Typography variant="caption" sx={{ color: BRAND_GREEN, fontWeight: 600 }}>
                    {project.project_phase}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>

          <Divider />
        </Paper>

        {/* CONTENT CARD */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,51,108,0.10)", boxShadow: "0 2px 12px rgba(0,51,108,0.08)", bgcolor: "white", overflow: "hidden" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: !isNew ? "640px 1fr" : "1fr" } }}>

            {/* LEFT — FORM */}
            <Box sx={{ borderRight: { lg: !isNew ? "1px solid rgba(0,51,108,0.08)" : "none" } }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Stack divider={<Divider />}>
                <Box sx={{ p: 3 }}>
                  <Box
                    sx={{
                      p: { xs: 2, md: 2.5 },
                      borderRadius: 2,
                      border: "1px solid rgba(0,51,108,0.06)",
                      bgcolor: "rgba(0,51,108,0.025)",
                    }}
                  >
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                      <Box>
                        <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 900, lineHeight: 1.15 }}>
                          Project Profile
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                          {projectCompletion.completed} of {projectCompletion.total} fields completed
                        </Typography>
                      </Box>
                      <Chip
                        label={`${projectCompletion.overall}% Complete`}
                        sx={{
                          bgcolor: "rgba(100,131,21,0.12)",
                          color: BRAND_GREEN,
                          border: "1px solid rgba(100,131,21,0.25)",
                          fontWeight: 900,
                        }}
                      />
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={projectCompletion.overall}
                      sx={{
                        height: 8,
                        borderRadius: 999,
                        bgcolor: "rgba(0,51,108,0.10)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          bgcolor: BRAND_GREEN,
                        },
                      }}
                    />
                  </Box>
                </Box>

                {/* SUMMARY */}
                <Box sx={{ p: 3 }}>
                  <SectionHeader>Summary</SectionHeader>
                  <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                    <TextField label="Project Name" value={project.project_name || ""} onChange={handleChange("project_name")} fullWidth required />

                    {isNew ? (
                      <CompanyAutocomplete
                        source="all"
                        selectedCompany={project.company_id ? { id: project.company_id, name: project.company, source: project.company_source || "bre" } : null}
                        onCompanySelect={(company) => {
                          const normalizedSource = company?.source === "prospecting" ? "prospecting" : "bre";
                          setProject((prev) => ({ ...prev, company_id: company?.id ?? null, company: company?.name ?? "", company_source: normalizedSource }));
                        }}
                      />
                    ) : project.company_id ? (
                      <CompanyAutocomplete
                        source="all"
                        selectedCompany={{ id: project.company_id, name: project.company, source: project.company_source || "bre" }}
                        onCompanySelect={(company) => {
                          const normalizedSource = company?.source === "prospecting" ? "prospecting" : "bre";
                          setProject((prev) => ({ ...prev, company_id: company?.id ?? null, company: company?.name ?? "", company_source: normalizedSource }));
                        }}
                      />
                    ) : (
                      <TextField label="Company" value={project.company || ""} InputProps={{ readOnly: true }} fullWidth />
                    )}

                    <TextField select label="Project Type" value={project.project_type} onChange={handleChange("project_type")} fullWidth>
                      {PROJECT_TYPES.map((type) => <MenuItem key={type.value} value={type.value}>{type.label}</MenuItem>)}
                    </TextField>

                    <FormControl fullWidth>
                      <InputLabel>Phase</InputLabel>
                      <Select label="Phase" value={project.project_phase || "Project Created"}
                        onChange={(e) => setProject((p) => ({ ...p, project_phase: normalizePhase(e.target.value) }))}
                      >
                        {PHASES.map((phase) => <MenuItem key={phase} value={phase}>{phase}</MenuItem>)}
                      </Select>
                    </FormControl>

                    <TextField label="Project Manager" value={project.project_manager} onChange={handleChange("project_manager")} fullWidth />

                    <DatePicker label="Start Date" value={getDateValue(project.start_date)} onChange={handleDateChange("start_date")} format="MM/DD/YYYY"
                      slotProps={{ textField: { fullWidth: true, sx: DATE_FIELD_SX } }}
                    />
                    <DatePicker label="End Date" value={getDateValue(project.end_date)} onChange={handleDateChange("end_date")} format="MM/DD/YYYY"
                      slotProps={{ textField: { fullWidth: true, sx: DATE_FIELD_SX } }}
                    />
                  </Stack>
                </Box>

                {/* LOCATION */}
                <Box sx={{ p: 3 }}>
                  <SectionHeader>Location</SectionHeader>
                  <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                    <CountryDropdown label="Country" value={project.country} onChange={handleChange("country")} fullWidth />
                    <StateDropdown label="State / Province" country={project.country} value={project.state} onChange={handleChange("state")} fullWidth />
                    <TextField label="City" value={project.city} onChange={handleChange("city")} fullWidth />
                  </Stack>
                </Box>

                {/* DETAILS */}
                <Box sx={{ p: 3 }}>
                  <SectionHeader>Details</SectionHeader>
                  <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                    <SectorDropdown
                      label="Sector"
                      value={project.sector}
                      onChange={handleChange("sector")}

                      formatOptionLabel={formatBroadSectorLabel}
                      sx={{
                        "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused, & .MuiInputBase-input, & .MuiSelect-icon": {
                          color: BRAND_BLUE,
                        },
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { "& .MuiMenuItem-root": { color: BRAND_BLUE } },
                          },
                        },
                      }}
                      fullWidth
                    />
                    <NaicsSelector value={project.naics_code || ""} title={project.naics_text || ""}
                      onChange={(code, title) => setProject((prev) => ({ ...prev, naics_code: code, naics_text: title }))}
                      onSectorChange={(sector) => setProject((prev) => ({ ...prev, sector }))} />
                    <TextField label="NAICS Text" value={project.naics_text} onChange={handleChange("naics_text")} fullWidth />
                    <TextField label="Custom Industry" value={project.industry_custom} onChange={handleChange("industry_custom")} fullWidth />
                    <TextField label="Investment Amount" type="number" value={project.investment_amount} onChange={handleChange("investment_amount")} fullWidth />
                    <TextField label="Jobs Created" type="number" value={project.jobs_created} onChange={handleChange("jobs_created")} fullWidth />
                    <TextField label="Average Wage Rate ($)" type="number" value={project.wage_rate} onChange={handleChange("wage_rate")} fullWidth />
                    <TextField select label="Required Skill Sets" value={project.skill_sets} onChange={handleMulti("skill_sets")}
                      SelectProps={{ multiple: true, renderValue: (selected) => selected.join(", ") }} fullWidth
                    >
                      {SKILL_SETS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          <Checkbox checked={project.skill_sets.includes(opt)} />
                          <ListItemText primary={opt} />
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Box>

                {/* SITE DETAILS (business_attraction) */}
                {isAttraction && (
                  <Box sx={{ p: 3 }}>
                    <SectionHeader>Site Details</SectionHeader>
                    <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                      <TextField label="Target Site" value={project.target_site} onChange={handleChange("target_site")} fullWidth />
                      <TextField label="Square Footage" type="number" value={project.square_footage} onChange={handleChange("square_footage")} fullWidth />
                      <TextField label="Acreage (acres)" type="number" value={project.acreage} onChange={handleChange("acreage")} fullWidth />
                      <FormControl fullWidth>
                        <InputLabel sx={{ color: BRAND_BLUE }}>Site Readiness</InputLabel>
                        <Select
                          label="Site Readiness"
                          value={project.site_readiness || ""}
                          onChange={handleChange("site_readiness")}
                          sx={{ color: BRAND_BLUE, "& .MuiSelect-icon": { color: BRAND_BLUE } }}
                        >
                          {SITE_READINESS_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt} sx={{ color: BRAND_BLUE }}>{opt}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <TextField label="Transportation Requirements (Separate requirements with commas.)" value={project.transportation_requirements} onChange={handleChange("transportation_requirements")} fullWidth />
                      <TextField label="Utility Requirements (Separate requirements with commas.)" value={project.utility_requirements} onChange={handleChange("utility_requirements")} fullWidth />
                      <TextField
                        label="Infrastructure Requirements (Separate requirements with commas.)"
                        value={project.infrastructure_requirements}
                        onChange={handleChange("infrastructure_requirements")}
                        fullWidth
                      />
                      <TextField label="Property Owner" value={project.property_owner} onChange={handleChange("property_owner")} fullWidth />
                    </Stack>
                  </Box>
                )}

                {/* EXPANSION DETAILS */}
                {isRetentionExpansion && (
                  <Box sx={{ p: 3 }}>
                    <SectionHeader>Expansion Details</SectionHeader>
                    <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                      <TextField label="Current Employees" type="number" value={project.current_employees} onChange={handleChange("current_employees")} fullWidth />
                      <TextField label="Expected New Hires" type="number" value={project.expected_new_hires} onChange={handleChange("expected_new_hires")} fullWidth />
                      <FormControl fullWidth>
                        <InputLabel>Expansion Type</InputLabel>
                        <Select label="Expansion Type" value={project.expansion_type || ""} onChange={handleChange("expansion_type")}>
                          {EXPANSION_TYPES.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField label="Facility Expansion Size (sq ft)" type="number" value={project.expansion_square_footage} onChange={handleChange("expansion_square_footage")} fullWidth />
                      <TextField
                        label="Infrastructure Requirements (Separate requirements with commas.)"
                        value={project.infrastructure_requirements}
                        onChange={handleChange("infrastructure_requirements")}
                        fullWidth
                      />
                      <TextField label="Transportation Requirements (Separate requirements with commas.)" value={project.transportation_requirements} onChange={handleChange("transportation_requirements")} fullWidth />
                      <TextField label="Utility Requirements (Separate requirements with commas.)" value={project.utility_requirements} onChange={handleChange("utility_requirements")} fullWidth />
                    </Stack>
                  </Box>
                )}

                {/* RETENTION DETAILS */}
                {isRetentionExpansion && (
                  <Box sx={{ p: 3 }}>
                    <SectionHeader>Retention Details</SectionHeader>
                    <Stack spacing={2.5} sx={{ maxWidth: 600 }}>
                      <ProjectMultiSelectField labelId="retention-risks-label" label="Retention Risks" value={project.retention_risks} onChange={handleMulti("retention_risks")} options={RETENTION_RISKS} />
                      <ProjectMultiSelectField labelId="support-needed-label" label="Support Needed" value={project.support_needed} onChange={handleMulti("support_needed")} options={SUPPORT_NEEDED} />
                      <FormControl fullWidth>
                        <InputLabel>Reason for Engagement</InputLabel>
                        <Select label="Reason for Engagement" value={project.reason_for_engagement || ""} onChange={handleChange("reason_for_engagement")}>
                          {REASONS_FOR_ENGAGEMENT.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                )}

                {/* SAVE */}
                <Box sx={{ p: 3, display: "flex", justifyContent: "flex-start" }}>
                  <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} sx={PRIMARY_BUTTON_SX}>
                    {isNew ? "Add Project" : "Save Changes"}
                  </Button>
                </Box>

              </Stack>
            </LocalizationProvider>
            </Box>

            {/* RIGHT — ACTIVITY FEED */}
            {!isNew && (
              <Box sx={{ p: 3, bgcolor: "rgba(0,51,108,0.012)", overflowY: "auto", maxHeight: "calc(100vh - 220px)", position: "sticky", top: 0 }}>
                <CompanyActivityTracker projectId={Number(id)} />
              </Box>
            )}

          </Box>
        </Paper>
      </Box>
    </Box>
  );
}

export default ProjectDetails;
